/**
 * Image picker: generate a new photograph, or reuse one already generated.
 *
 * Reuse comes first in the interface. Generating costs money and takes twenty
 * seconds, and most of the time the right photograph is one that already
 * exists — a gallery placed after a "generate" button is a gallery nobody
 * scrolls to.
 *
 * The alt text is a required field, not an optional one. An image without it
 * simply does not exist for someone using a screen reader, and on a page a
 * public body publishes that is not a detail to be filled in later.
 */

import type { ImagemBloco } from '@egba-do/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type Item } from '../../api/cliente'
import { AreaTexto, Aviso, Botao, Campo, Selecao } from '../ui'

const URL_API = import.meta.env.VITE_API_URL ?? ''

interface MidiaLista {
  id: string
  alt: string
  prompt: string
  largura: number
  altura: number
  url: string
}

const PROPORCOES = [
  { valor: '16:9', texto: 'Larga (16:9) — destaque e faixa' },
  { valor: '4:3', texto: 'Clássica (4:3)' },
  { valor: '1:1', texto: 'Quadrada (1:1)' },
  { valor: '3:4', texto: 'Retrato (3:4)' },
]

export interface EscolhaImagemProps {
  imagem: ImagemBloco | null
  /** Block text, used to pre-fill the description. */
  sugestao?: string
  onEscolher: (img: ImagemBloco) => void
  onRemover: () => void
}

export function EscolhaImagem({ imagem, sugestao, onEscolher, onRemover }: EscolhaImagemProps) {
  const [aberto, setAberto] = useState(false)
  const [descricao, setDescricao] = useState('')
  const [alt, setAlt] = useState('')
  const [proporcao, setProporcao] = useState('16:9')
  const [erro, setErro] = useState<string | null>(null)

  const { data: galeria } = useQuery({
    queryKey: ['midias'],
    queryFn: () => api.get<{ dados: MidiaLista[] }>('/api/v1/estudio/midias'),
    enabled: aberto,
  })

  const gerar = useMutation({
    mutationFn: () =>
      api.post<Item<{ imagem: ImagemBloco }>>('/api/v1/estudio/imagem', {
        descricao,
        alt,
        proporcao,
      }),
    onSuccess: (r) => {
      onEscolher(r.dados.imagem)
      setAberto(false)
      setErro(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Imagem</p>

      {imagem ? (
        <div className="space-y-2">
          <img
            src={`${URL_API}${imagem.url}?l=800&f=jpeg`}
            alt={imagem.alt}
            className="w-full rounded-lg border border-[color:var(--color-borda)]"
          />
          <p className="text-xs text-slate-500">{imagem.alt}</p>
          <div className="flex gap-2">
            <Botao variante="secundario" tamanho="sm" onClick={() => setAberto(true)}>
              Trocar
            </Botao>
            <Botao variante="secundario" tamanho="sm" onClick={onRemover}>
              Remover
            </Botao>
          </div>
        </div>
      ) : (
        <Botao
          variante="secundario"
          tamanho="sm"
          onClick={() => {
            setDescricao('')
            setAlt(sugestao ?? '')
            setAberto(true)
          }}
        >
          Escolher imagem
        </Botao>
      )}

      {aberto && (
        <div className="space-y-4 rounded-lg border border-[color:var(--color-borda)] bg-slate-50/60 p-3">
          {erro && <Aviso tom="perigo">{erro}</Aviso>}

          {/* --------------------------------------------------- reaproveitar */}
          {galeria?.dados.length ? (
            <div>
              <p className="mb-2 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                Já geradas
              </p>
              <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto">
                {galeria.dados.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    title={m.alt}
                    onClick={() => {
                      onEscolher({
                        url: m.url,
                        alt: m.alt,
                        largura: m.largura,
                        altura: m.altura,
                      })
                      setAberto(false)
                    }}
                    className="overflow-hidden rounded-md border border-[color:var(--color-borda)] transition hover:border-marca-400 hover:ring-2 hover:ring-marca-200"
                  >
                    <img
                      src={`${URL_API}${m.url}?l=800&f=jpeg`}
                      alt={m.alt}
                      loading="lazy"
                      className="aspect-video w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* --------------------------------------------------------- gerar */}
          <div className="space-y-3 border-t border-[color:var(--color-borda-suave)] pt-3">
            <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
              Gerar nova
            </p>

            <AreaTexto
              rotulo="Descreva a cena"
              rows={4}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              ajuda="Quem aparece, onde, fazendo o quê e com que luz. O sistema já pede foto documental brasileira, sem texto e sem logotipo."
            />

            <Campo
              rotulo="Texto alternativo"
              obrigatorio
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              ajuda="É o que um leitor de tela vai narrar. Descreva a cena, não o arquivo."
            />

            <Selecao
              rotulo="Proporção"
              value={proporcao}
              onChange={(e) => setProporcao(e.target.value)}
              opcoes={PROPORCOES}
            />

            <div className="flex justify-end gap-2">
              <Botao variante="secundario" tamanho="sm" onClick={() => setAberto(false)}>
                Fechar
              </Botao>
              <Botao
                tamanho="sm"
                carregando={gerar.isPending}
                disabled={descricao.trim().length < 15 || alt.trim().length < 5}
                onClick={() => gerar.mutate()}
              >
                {gerar.isPending ? 'Gerando… (~20s)' : 'Gerar imagem'}
              </Botao>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
